import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Package, Truck, Bell } from "lucide-react";
import { API_URL } from "@/utils/apiConfig";
import QueueCard from "@/components/estafeta/QueueCard";
import FoodSafetyNotice from "@/components/estafeta/FoodSafetyNotice";
import TrackingMap from "@/components/estafeta/TrackingMap";
import TrackingTimeline from "@/components/estafeta/TrackingTimeline";
import TrackingOrderInfo from "@/components/estafeta/TrackingOrderInfo";
import DeliveredCard from "@/components/estafeta/DeliveredCard";
import { TRACKING_STRINGS, detectTrackingLang } from "@/components/estafeta/trackingStrings";

const BACKEND_URL = API_URL;

export default function CustomerTrackingPage() {
  const { orderId } = useParams();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushAsked, setPushAsked] = useState(false);
  const [lang, setLang] = useState("es");
  const tt = (key) => TRACKING_STRINGS[lang]?.[key] || TRACKING_STRINGS.es[key] || key;

  const fetchTracking = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/tracking/public/${orderId}`);
      if (!res.ok) throw new Error("Pedido no encontrado");
      const body = await res.json();
      const data = body?.data ?? body;
      setTracking(data);
      if (data?.restaurant_locale) setLang(detectTrackingLang(data.restaurant_locale));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [orderId]);

  const registerPush = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    try {
      const reg = await navigator.serviceWorker.register("/sw-push.js");
      await navigator.serviceWorker.ready;
      const res = await fetch(`${BACKEND_URL}/api/v1/push/vapid-key`);
      const body = await res.json();
      const vapidData = body?.data ?? body;
      const publicKey = vapidData?.public_key;
      if (!publicKey) return;
      const permission = await Notification.requestPermission();
      if (permission !== "granted") return;
      const padding = "=".repeat((4 - (publicKey.length % 4)) % 4);
      const b64 = (publicKey + padding).replace(/-/g, "+").replace(/_/g, "/");
      const raw = window.atob(b64);
      const key = Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
      const subscription = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
      await fetch(`${BACKEND_URL}/api/v1/push/subscribe-customer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: orderId, subscription: subscription.toJSON() }),
      });
      setPushEnabled(true);
      localStorage.setItem(`push_${orderId}`, "1");
    } catch (e) {
      console.warn("Push registration failed:", e);
    }
  }, [orderId]);

  useEffect(() => {
    fetchTracking();
    const interval = setInterval(fetchTracking, 5000);
    return () => clearInterval(interval);
  }, [fetchTracking]);

  useEffect(() => {
    if (localStorage.getItem(`push_${orderId}`)) { setPushEnabled(true); setPushAsked(true); }
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando rastreamiento...</p>
        </div>
      </div>
    );
  }

  if (error || !tracking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-700 mb-2">Pedido no encontrado</h1>
          <p className="text-gray-500">{error || "El link puede haber expirado"}</p>
        </div>
      </div>
    );
  }

  const isDelivering = tracking.status === "delivering";
  const isDelivered = tracking.status === "delivered";
  const mapAllowed = tracking.tracking_map_enabled !== false;
  const hasMap = mapAllowed && (tracking.deliverer_location || isDelivering);
  const showQueueCard = !isDelivered && tracking.delivery_queue_position > 0;
  const restaurantPos = [tracking.restaurant_lat, tracking.restaurant_lng];
  const orderSource = (tracking.order_type || tracking.source || "").toLowerCase();
  const isPickupOrder = tracking.is_pickup ||
    ["pickup", "takeaway", "recogida", "retirada", "balcao", "balcão"].some(k => orderSource.includes(k));
  const isDeliveryOrder = !isPickupOrder && (
    ["delivery", "envio", "envío"].some(k => orderSource.includes(k)) || !!tracking.delivery_address
  );
  const brandColor = tracking.primary_color || "#059669";
  const isPickedUp = isDelivered && isPickupOrder;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="customer-tracking-page">
      <style>{`@keyframes pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.1)}}`}</style>
      <div className="bg-emerald-600 text-white px-4 py-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-lg font-bold">{tracking.restaurant_name}</h1>
            <Badge className="bg-white/20 text-white">#{orderId.slice(-6).toUpperCase()}</Badge>
          </div>
          {tracking.customer_name && (
            <p className="text-emerald-100 text-sm">{tt("hello")}, {tracking.customer_name}</p>
          )}
        </div>
      </div>
      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {!pushEnabled && !pushAsked && tracking.status !== "delivered" && "Notification" in window && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center gap-3" data-testid="push-banner">
            <Bell className="w-8 h-8 text-blue-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">{tt("receiveNotifs")}</p>
              <p className="text-xs text-blue-600">{tt("notifDesc")}</p>
            </div>
            <button
              onClick={() => { setPushAsked(true); registerPush(); }}
              className="bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors"
              data-testid="enable-push-btn"
            >
              {tt("activate")}
            </button>
          </div>
        )}
        {pushEnabled && !pushAsked && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3 text-center text-sm text-emerald-700" data-testid="push-enabled-msg">
            {tt("notifsEnabled")}
          </div>
        )}
        {isDelivering && tracking.eta_minutes && (
          <div className="bg-white rounded-2xl shadow-sm border p-5 text-center" data-testid="eta-card">
            <p className="text-gray-500 text-sm mb-1">{tt("eta")}</p>
            <p className="text-4xl font-bold text-emerald-600">{tracking.eta_minutes} min</p>
            {tracking.deliverer_name && (
              <p className="text-sm text-gray-500 mt-2 flex items-center justify-center gap-1">
                <Truck className="w-4 h-4" /> {tracking.deliverer_name} {tt("driverOnWay")}
              </p>
            )}
          </div>
        )}
        {isDelivered && (
          <DeliveredCard isPickup={isPickedUp} restaurantId={tracking.restaurant_id} orderId={orderId} tt={tt} />
        )}
        <FoodSafetyNotice status={tracking.status} isDelivery={isDeliveryOrder} isPickup={isPickupOrder} brandColor={brandColor} />
        {showQueueCard && <QueueCard tracking={tracking} lang={lang} />}
        <TrackingTimeline timeline={tracking.timeline} delivererName={tracking.deliverer_name} tt={tt} />
        {hasMap && <TrackingMap tracking={tracking} restaurantPos={restaurantPos} tt={tt} />}
        <TrackingOrderInfo tracking={tracking} orderId={orderId} tt={tt} />
        <div className="text-center pb-8">
          <p className="text-xs text-gray-400">{tt("poweredBy")} Kaiso System</p>
        </div>
      </div>
    </div>
  );
}
